import {inject, Injectable, signal} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_SCENE} from "@common/services/logging"
import {
    IMeshGeometryQueries,
    ReifiedMeshData,
    MeshPointData,
    SceneNodeMeshSchema,
    SceneNodes,
    MeshGeometryQueries,
    barycentricCoordinates,
    interpolateAttribute,
} from "@cm/template-nodes"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {Vector2, Vector3} from "@cm/math"
import {ThreeMesh} from "@app/template-editor/helpers/three-mesh"
import {getNextThreeObjectPart} from "@app/template-editor/helpers/three-object"
import {Three as THREE} from "@cm/material-nodes/three"
import {EMPTY, type Observable} from "rxjs"

export {barycentricCoordinates, interpolateAttribute}

@Injectable()
export class MeshGeometryService implements IMeshGeometryQueries {
    readonly threeSceneManagerService = signal<ThreeSceneManagerService | undefined>(undefined)
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)
    private readonly meshGeometryQueries = new MeshGeometryQueries(this.logger)

    projectPointToMeshInUVSpace(mesh: ReifiedMeshData, meshUVChannel: number, pointUV: Vector2): MeshPointData | null {
        return this.meshGeometryQueries.projectPointToMeshInUVSpace(mesh, meshUVChannel, pointUV)
    }

    raycastScene(origin: Vector3, direction: Vector3): number | null {
        const threeSceneManagerService = this.threeSceneManagerService()
        if (!threeSceneManagerService) return null

        const raycaster = new THREE.Raycaster()
        raycaster.set(new THREE.Vector3(origin.x, origin.y, origin.z), new THREE.Vector3(direction.x, direction.y, direction.z).normalize())

        for (const {distance, object} of raycaster.intersectObjects(threeSceneManagerService.getSceneReference().children, true)) {
            if (!object.visible || distance <= 0) continue
            const threeObjectPart = getNextThreeObjectPart(object)
            if (threeObjectPart?.threeObject instanceof ThreeMesh) return distance
        }
        return null
    }

    sceneGeometryChanged(): Observable<void> {
        const threeSceneManagerService = this.threeSceneManagerService()
        if (!threeSceneManagerService) return EMPTY
        return threeSceneManagerService.sceneGeometryChanged$
    }

    projectPointToMeshInPositionSpace(mesh: ReifiedMeshData, point: Vector3): MeshPointData | null {
        const threeMesh = this.findThreeMeshForReifiedMesh(mesh)
        if (threeMesh) threeMesh.ensureBoundsTree()

        const threeSubMeshes = threeMesh?.getSubMeshes()
        const prebuiltSubMeshes = threeSubMeshes?.length
            ? threeSubMeshes.map(({subMesh, materialIndex}) => ({geometry: subMesh.geometry, materialIndex}))
            : undefined

        return this.meshGeometryQueries.projectPointToMeshInPositionSpace(mesh, point, prebuiltSubMeshes)
    }

    private findThreeMeshForReifiedMesh(mesh: ReifiedMeshData): ThreeMesh | null {
        const threeSceneManagerService = this.threeSceneManagerService()
        if (!threeSceneManagerService) throw new Error("ThreeSceneManagerService not available")

        const {sceneManagerService} = threeSceneManagerService

        const findSceneMeshNode = (node: SceneNodes.SceneNode): node is SceneNodes.Mesh => {
            const meshNode = SceneNodeMeshSchema.safeParse(node)
            if (!meshNode.success) return false
            return meshNode.data.completeMeshData.reified.contentHash === mesh.contentHash
        }

        const sceneMeshNode = sceneManagerService.$scene().find(findSceneMeshNode)
        if (!sceneMeshNode) return null
        const threeMesh = threeSceneManagerService.getThreeObject(sceneMeshNode.id)
        return threeMesh instanceof ThreeMesh ? threeMesh : null
    }
}
